import React, { useState } from 'react'
import styled from '@emotion/styled'
import { ScheduleRule, DayOfWeek, Schedule } from '../types'
import { DAY_OF_WEEK_LABELS, DAY_OF_WEEK_FULL_LABELS } from '../constants'
import { formatCutoffTime, parseCutoffTimeInput } from '../utils'

interface Props {
  schedule: Schedule
  rules: ScheduleRule[]
  onAddRule: (cycleWeek: number, dayOfWeek: DayOfWeek) => void
  onUpdateRule: (id: string, partial: Partial<ScheduleRule>) => void
  onRemoveRule: (id: string) => void
}

const ALL_DAYS: DayOfWeek[] = [
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
  DayOfWeek.Saturday,
  DayOfWeek.Sunday,
]

const WeekCycleRulesEditor: React.FC<Props> = ({
  schedule,
  rules,
  onAddRule,
  onUpdateRule,
  onRemoveRule,
}) => {
  const [expandedRule, setExpandedRule] = useState<string | null>(null)

  if (schedule.cycleLengthWeeks === null) {
    return (
      <InfoText>
        No recurring schedule — this client uses ad hoc days only. Add service
        days via overrides below.
      </InfoText>
    )
  }

  const cycleWeeks = Array.from(
    { length: schedule.cycleLengthWeeks },
    (_, i) => i + 1,
  )

  const getRuleForCell = (
    cycleWeek: number,
    dayOfWeek: DayOfWeek,
  ): ScheduleRule | undefined =>
    rules.find((r) => r.cycleWeek === cycleWeek && r.dayOfWeek === dayOfWeek)

  const handleToggle = (cycleWeek: number, dayOfWeek: DayOfWeek) => {
    const existing = getRuleForCell(cycleWeek, dayOfWeek)
    if (existing) {
      onRemoveRule(existing.id)
      if (expandedRule === existing.id) {
        setExpandedRule(null)
      }
    } else {
      onAddRule(cycleWeek, dayOfWeek)
    }
  }

  const handleCutoffToggle = (rule: ScheduleRule) => {
    if (expandedRule === rule.id) {
      setExpandedRule(null)
    } else {
      setExpandedRule(rule.id)
    }
  }

  return (
    <Container>
      {/* Header row */}
      <Grid>
        <HeaderCell>{schedule.cycleLengthWeeks > 1 ? 'Week' : ''}</HeaderCell>
        {ALL_DAYS.map((day) => (
          <HeaderCell key={day}>{DAY_OF_WEEK_LABELS[day]}</HeaderCell>
        ))}
      </Grid>

      {/* One row per cycle week */}
      {cycleWeeks.map((week) => (
        <div key={week}>
          <Grid>
            <WeekLabel>
              {(schedule.cycleLengthWeeks ?? 1) > 1 ? `Week ${week}` : 'Days'}
            </WeekLabel>
            {ALL_DAYS.map((day) => {
              const rule = getRuleForCell(week, day)
              const isActive = !!rule
              const isWeekend =
                day === DayOfWeek.Saturday || day === DayOfWeek.Sunday

              return (
                <DayCell
                  key={day}
                  data-testid={`rule-cell-${week}-${day}`}
                  isActive={isActive}
                  isWeekend={isWeekend}
                  onClick={() => handleToggle(week, day)}
                  title={`${DAY_OF_WEEK_FULL_LABELS[day]}${
                    isActive ? ' (click to remove)' : ' (click to add)'
                  }`}
                >
                  {isActive && '✓'}
                </DayCell>
              )
            })}
          </Grid>

          {/* Expanded cutoff editors for active rules in this week */}
          {rules
            .filter((r) => r.cycleWeek === week)
            .map((rule) => (
              <div key={rule.id}>
                <CutoffToggle
                  onClick={() => handleCutoffToggle(rule)}
                  data-testid={`cutoff-toggle-${rule.id}`}
                >
                  {DAY_OF_WEEK_FULL_LABELS[rule.dayOfWeek]}
                  {rule.cutoffDaysBefore != null
                    ? ` — Cutoff: ${
                        rule.cutoffDaysBefore
                      }d @ ${formatCutoffTime(rule.cutoffTime ?? 0)}`
                    : ''}
                  {rule.dropoffTime != null
                    ? ` | Dropoff: ${formatCutoffTime(rule.dropoffTime)}`
                    : ''}
                  {rule.cutoffDaysBefore == null && rule.dropoffTime == null
                    ? ' — Using defaults'
                    : ''}
                  <ExpandIcon>
                    {expandedRule === rule.id ? '▲' : '▼'}
                  </ExpandIcon>
                </CutoffToggle>

                {expandedRule === rule.id && (
                  <CutoffEditor data-testid={`cutoff-editor-${rule.id}`}>
                    <label>
                      <input
                        type="checkbox"
                        checked={rule.cutoffDaysBefore != null}
                        onChange={(e) => {
                          if (e.target.checked) {
                            onUpdateRule(rule.id, {
                              cutoffDaysBefore:
                                schedule.defaultCutoffDaysBefore,
                              cutoffTime: schedule.defaultCutoffTime,
                            })
                          } else {
                            onUpdateRule(rule.id, {
                              cutoffDaysBefore: null,
                              cutoffTime: null,
                            })
                          }
                        }}
                      />{' '}
                      Override cutoff for{' '}
                      {DAY_OF_WEEK_FULL_LABELS[rule.dayOfWeek]}
                    </label>
                    {rule.cutoffDaysBefore != null && (
                      <CutoffFields>
                        <label>
                          Days Before:
                          <input
                            type="number"
                            min={0}
                            max={14}
                            value={rule.cutoffDaysBefore}
                            onChange={(e) =>
                              onUpdateRule(rule.id, {
                                cutoffDaysBefore:
                                  parseInt(e.target.value, 10) || 0,
                              })
                            }
                          />
                        </label>
                        <label>
                          Time:
                          <input
                            type="time"
                            value={formatCutoffTime(rule.cutoffTime ?? 0)}
                            onChange={(e) =>
                              onUpdateRule(rule.id, {
                                cutoffTime: parseCutoffTimeInput(
                                  e.target.value,
                                ),
                              })
                            }
                          />
                        </label>
                      </CutoffFields>
                    )}

                    <SectionDivider />

                    <label>
                      <input
                        type="checkbox"
                        checked={rule.dropoffTime != null}
                        onChange={(e) => {
                          if (e.target.checked) {
                            onUpdateRule(rule.id, { dropoffTime: 1130 })
                          } else {
                            onUpdateRule(rule.id, { dropoffTime: null })
                          }
                        }}
                      />{' '}
                      Override dropoff for{' '}
                      {DAY_OF_WEEK_FULL_LABELS[rule.dayOfWeek]}
                    </label>
                    {rule.dropoffTime != null && (
                      <CutoffFields>
                        <label>
                          Dropoff Time:
                          <input
                            type="time"
                            value={formatCutoffTime(rule.dropoffTime)}
                            onChange={(e) =>
                              onUpdateRule(rule.id, {
                                dropoffTime: parseCutoffTimeInput(
                                  e.target.value,
                                ),
                              })
                            }
                          />
                        </label>
                      </CutoffFields>
                    )}
                  </CutoffEditor>
                )}
              </div>
            ))}
        </div>
      ))}
    </Container>
  )
}

export default WeekCycleRulesEditor

// ── Styled Components ──

const Container = styled.div`
  margin-top: 8px;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: 56px repeat(7, 1fr);
  gap: 3px;
  margin-bottom: 3px;
`

const HeaderCell = styled.div`
  text-align: center;
  font-weight: 600;
  font-size: 10px;
  color: #616161;
  padding: 4px 0;
`

const WeekLabel = styled.div`
  display: flex;
  align-items: center;
  font-size: 10px;
  font-weight: 500;
  color: #616161;
  padding-left: 2px;
`

const DayCell = styled.div<{ isActive: boolean; isWeekend: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: background-color 0.15s;
  background-color: ${({ isActive, isWeekend }) =>
    isActive ? '#81C784' : isWeekend ? '#F5F5F5' : '#EEEEEE'};
  color: ${({ isActive }) => (isActive ? '#fff' : '#9E9E9E')};

  &:hover {
    background-color: ${({ isActive }) => (isActive ? '#66BB6A' : '#E0E0E0')};
  }
`

const CutoffToggle = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 4px 8px;
  margin: 2px 0;
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  color: #424242;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    background: #f5f5f5;
  }
`

const ExpandIcon = styled.span`
  margin-left: auto;
  font-size: 10px;
  color: #9e9e9e;
`

const CutoffEditor = styled.div`
  padding: 8px 12px;
  margin: 2px 0 6px;
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 13px;

  label {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
  }
`

const CutoffFields = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
  padding-left: 20px;

  label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
  }

  input[type='number'] {
    width: 60px;
    padding: 4px;
    border: 1px solid #bdbdbd;
    border-radius: 3px;
  }

  input[type='time'] {
    padding: 4px;
    border: 1px solid #bdbdbd;
    border-radius: 3px;
  }
`

const SectionDivider = styled.hr`
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 8px 0;
`

const InfoText = styled.p`
  color: #757575;
  font-size: 13px;
  font-style: italic;
  margin: 8px 0;
`
